import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { api } from '../lib/api.js';
import { useAuth } from '../lib/auth.jsx';
import { useProfile } from '../lib/profile.jsx';
import { useIsMobile } from '../lib/useIsMobile.js';
import { vtNavigate } from '../lib/viewTransition.js';
import { YourActions } from './ClientHome.jsx';
import Icon from '../components/Icon.jsx';
import { MAKE_IT_HAPPEN } from '../lib/makeItHappen.js'; // one label per capability (shared with home)

// Actions hub — every suggested action from a digest/briefing as a card the
// Owl can act on. INVESTIGATE cards (read-only analysis) run automatically when
// auto-investigate is on and post a grounded finding; EXECUTE cards deep-link
// into the pre-filled campaign editor and always wait for human approval.
// Mobile-first: one column of cards; the "Your actions" strip lives at the end.

const md = (s) => String(s || '').split('**').map((part, i) => (i % 2 ? <b key={i}>{part}</b> : part));
const fmtDay = (iso) => { try { return new Date(iso).toLocaleDateString('en-ZA', { weekday: 'short', day: 'numeric', month: 'short' }); } catch { return ''; } };

export default function ActionsHubPage() {
  const { isAdmin } = useAuth();
  const { activeEntityId } = useProfile();
  const { previewEntityId } = useOutletContext() || {};
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  // The client in context: the previewed client (admin) or the active profile.
  const entityId = previewEntityId || (isAdmin ? null : activeEntityId);
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState('open'); // open | answered | dismissed | all
  const [busy, setBusy] = useState({}); // id -> true while investigating
  const [open, setOpen] = useState({}); // id -> finding expanded

  const load = () => { if (entityId) api.getActionsHub(entityId).then(setData).catch(() => setData({ items: [], auto: true })); };
  useEffect(() => { load(); }, [entityId]); // eslint-disable-line react-hooks/exhaustive-deps
  // While anything is investigating (auto or tapped), poll so findings appear live.
  useEffect(() => {
    if (!data?.items?.some((i) => i.status === 'investigating') && !Object.keys(busy).length) return;
    const t = setInterval(load, 6000);
    return () => clearInterval(t);
  }, [data, busy]); // eslint-disable-line react-hooks/exhaustive-deps

  const patch = (item) => setData((d) => ({ ...d, items: d.items.map((i) => (i.id === item.id ? item : i)) }));
  // A human actually SET SOMETHING GOING from a card (not an auto-investigation)
  // — the signal behind the "Act on a suggested action" onboarding step.
  const trackAct = () => { if (entityId) api.trackUsage(entityId, { kind: 'feature', name: 'actions_hub_act', event: 'use' }); };
  const investigate = async (it) => {
    trackAct();
    setBusy((b) => ({ ...b, [it.id]: true }));
    patch({ ...it, status: 'investigating' });
    try { const r = await api.hubInvestigate(entityId, it.id); patch(r.item); setOpen((o) => ({ ...o, [it.id]: true })); }
    catch (e) { alert(e.message); load(); }
    finally { setBusy((b) => { const n = { ...b }; delete n[it.id]; return n; }); }
  };
  const dismiss = (it, restore = false) => api.hubDismiss(entityId, it.id, restore).then((r) => patch(r.item)).catch((e) => alert(e.message));
  const recheck = (it, days) => api.hubRecheck(entityId, it.id, days).then((r) => patch(r.item)).catch((e) => alert(e.message));
  const follow = (it) => { trackAct(); return api.hubFollow(entityId, it.id).then((r) => patch(r.item)).catch((e) => alert(e.message)); };
  const toggleAuto = () => api.setHubAuto(entityId, !data.auto).then((r) => setData((d) => ({ ...d, auto: r.auto }))).catch((e) => alert(e.message));

  const items = data?.items || [];
  const counts = useMemo(() => ({
    open: items.filter((i) => !['dismissed', 'answered', 'done'].includes(i.status)).length,
    answered: items.filter((i) => ['answered', 'done'].includes(i.status)).length,
    dismissed: items.filter((i) => i.status === 'dismissed').length,
    all: items.length,
  }), [items]);
  const shown = items.filter((i) => filter === 'all' ? true
    : filter === 'answered' ? ['answered', 'done'].includes(i.status)
    : filter === 'dismissed' ? i.status === 'dismissed'
    : !['dismissed', 'answered', 'done'].includes(i.status));

  if (!entityId) return <p style={{ color: 'var(--muted)', fontSize: 13, padding: 20 }}>{isAdmin ? 'Open a client suite first so the preview knows which client to show.' : 'No client account is linked to your login yet.'}</p>;
  if (!data) return <p style={{ color: 'var(--muted)', fontSize: 13, padding: 20 }}>Loading…</p>;

  return (
    <div style={{ maxWidth: 640, margin: '0 auto', padding: isMobile ? '14px 12px 40px' : '20px 16px 48px', display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div>
        <h1 style={{ fontSize: 20, fontWeight: 800, margin: '0 0 2px', display: 'flex', alignItems: 'center', gap: 8 }}><Icon name="spark" size={20} /> Actions</h1>
        <p style={{ fontSize: 12.5, color: 'var(--muted)', margin: 0 }}>Suggestions from your briefings & digests, ready for the Owl to act on. Investigations are read-only; anything that sends always waits for your approval.</p>
      </div>

      {/* Auto-investigate switch */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'var(--card,#fff)', border: '1px solid var(--hairline)', borderRadius: 12, padding: '9px 12px', fontSize: 12, color: 'var(--muted)' }}>
        <span>Auto-investigate is <b style={{ color: 'var(--text)' }}>{data.auto ? 'on' : 'off'}</b>{data.auto ? ' — findings run as suggestions arrive' : ' — tap a card to run it'}</span>
        <button
          onClick={toggleAuto} role="switch" aria-checked={data.auto} title="Toggle auto-investigate"
          style={{ marginLeft: 'auto', width: 42, height: 25, borderRadius: 980, border: 'none', cursor: 'pointer', flexShrink: 0, position: 'relative', background: data.auto ? 'var(--success,#10b981)' : 'var(--hairline)' }}
        >
          <span style={{ position: 'absolute', top: 3, left: data.auto ? 20 : 3, width: 19, height: 19, borderRadius: '50%', background: '#fff', transition: 'left .15s' }} />
        </button>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 2 }}>
        {[['open', 'To review'], ['answered', 'Answered'], ['dismissed', 'Dismissed'], ['all', 'All']].map(([k, label]) => (
          <button key={k} onClick={() => setFilter(k)}
            style={{ flexShrink: 0, fontSize: 11.5, fontWeight: 700, padding: '6px 13px', borderRadius: 980, cursor: 'pointer', minHeight: 30,
              border: `1px solid ${filter === k ? 'var(--text)' : 'var(--hairline)'}`, background: filter === k ? 'var(--text)' : 'transparent', color: filter === k ? 'var(--bg,#fff)' : 'var(--muted)' }}>
            {label} · {counts[k]}
          </button>
        ))}
      </div>

      {shown.length === 0 && (
        <div style={{ background: 'var(--card,#fff)', border: '1px solid var(--hairline)', borderRadius: 14, padding: 22, textAlign: 'center', color: 'var(--muted)', fontSize: 13 }}>
          {items.length === 0 ? 'Nothing here yet — suggested actions land automatically as your briefings and digests go out.' : 'Nothing in this view.'}
        </div>
      )}

      {shown.map((it) => <ActionCard key={it.id} it={it} busy={!!busy[it.id]} open={!!open[it.id]}
        onToggle={() => setOpen((o) => ({ ...o, [it.id]: !o[it.id] }))}
        onInvestigate={() => investigate(it)} onDismiss={() => dismiss(it)} onRestore={() => dismiss(it, true)}
        onGo={() => { if (it.href) { trackAct(); window.location.href = it.href; } }}
        onNext={(href) => { trackAct(); vtNavigate(navigate, href); }}
        onRecheck={(days) => recheck(it, days)} onFollow={() => follow(it)} />)}

      {/* The housed "Your actions" strip — recent campaigns & their performance */}
      <div style={{ marginTop: 8 }}>
        <YourActions entityId={entityId} isMobile={isMobile} onOpen={() => vtNavigate(navigate, '/engage/campaigns')} />
      </div>
    </div>
  );
}

function ActionCard({ it, busy, open, onToggle, onInvestigate, onDismiss, onRestore, onGo, onNext, onRecheck, onFollow }) {
  const [pickRecheck, setPickRecheck] = useState(false); // show the when-chips
  const isInv = it.type === 'investigate';
  const done = it.status === 'done';
  const accent = isInv ? 'var(--owl,#5856d6)' : 'var(--brand)';
  const dim = it.status === 'dismissed';
  const f = it.status === 'answered' ? it.finding : null;
  const failed = it.status === 'failed';
  const chip = (label, c, bg) => <span style={{ fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em', padding: '3px 9px', borderRadius: 980, color: c, background: bg }}>{label}</span>;
  const statusChip = it.status === 'answered' ? chip('✓ Answered', 'var(--success,#2da44e)', 'rgba(52,199,89,0.14)')
    : done ? chip('✓ Done', 'var(--success,#2da44e)', 'rgba(52,199,89,0.14)')
    : it.status === 'investigating' ? chip('Investigating…', '#b45309', 'rgba(245,158,11,0.15)')
    : failed ? chip('Couldn’t answer', '#dc2626', 'rgba(239,68,68,0.12)')
    : dim ? chip('Dismissed', 'var(--muted)', 'var(--hairline)')
    : chip(isInv ? 'New' : 'Ready to draft', '#0a66c2', 'rgba(10,132,255,0.12)');
  // A scheduled re-check shows on the card until it runs (or is cancelled).
  const recheckLabel = it.recheckAt ? fmtDay(it.recheckAt) : '';

  return (
    <div style={{ background: 'var(--card,#fff)', border: '1px solid var(--hairline)', borderLeft: `3px solid ${accent}`, borderRadius: 14, padding: '13px 14px', display: 'flex', flexDirection: 'column', gap: 8, opacity: dim ? 0.55 : 1 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        {chip(isInv ? 'Investigate' : 'Execute', accent, isInv ? 'rgba(88,86,214,0.10)' : 'rgba(255,56,92,0.10)')}
        {statusChip}
        <span style={{ marginLeft: 'auto', fontSize: 10.5, color: 'var(--muted)' }}>{it.source === 'digest' ? 'Digest' : 'Briefing'} · {fmtDay(it.createdAt)}</span>
      </div>
      <div style={{ fontSize: 14.5, fontWeight: 700, lineHeight: 1.35 }}>{it.title}</div>
      {it.reason && !f && <div style={{ fontSize: 12, color: 'var(--muted)' }}>{md(it.reason)}</div>}
      {!dim && !f && !failed && !done && (
        <div style={{ fontSize: 12, color: 'var(--muted)', borderLeft: '2px solid var(--hairline)', paddingLeft: 9 }}>
          <b style={{ color: 'var(--text)', fontWeight: 650 }}>How the Owl handles this:</b> {it.plan}
        </div>
      )}
      {failed && it.finding?.error && <div style={{ fontSize: 12, color: '#dc2626' }}>{it.finding.error}</div>}
      {done && it.finding?.followed && (
        <div style={{ fontSize: 12, color: 'var(--muted)' }}>
          Now following <b style={{ color: 'var(--text)' }}>{it.finding.followed.tileTitle}</b> ({it.finding.followed.dashTitle}) — every briefing and digest will cover it. Unfollow any time from the tile itself.
        </div>
      )}

      {f && open && (
        <div style={{ background: 'rgba(88,86,214,0.08)', borderRadius: 11, padding: '11px 12px', display: 'flex', flexDirection: 'column', gap: 7 }}>
          <span style={{ fontSize: 10.5, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--owl,#5856d6)' }}>The Owl’s finding</span>
          <div style={{ fontSize: 13.5, fontWeight: 650, lineHeight: 1.4 }}>{md(f.headline)}</div>
          {(f.bullets || []).length > 0 && (
            <ul style={{ margin: 0, paddingLeft: 17, fontSize: 12.5, display: 'flex', flexDirection: 'column', gap: 4 }}>
              {f.bullets.map((b, i) => <li key={i}>{md(b)}</li>)}
            </ul>
          )}
          {f.caveat && <div style={{ fontSize: 11.5, color: 'var(--muted)', fontStyle: 'italic' }}>{md(f.caveat)}</div>}
          {(f.evidence || []).length > 0 && (
            <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
              {f.evidence.slice(0, 6).map((e, i) => (
                <span key={i} style={{ fontSize: 10.5, background: 'var(--card,#fff)', border: '1px solid var(--hairline)', borderRadius: 6, padding: '2px 7px', color: 'var(--muted)' }}>
                  {e.title} · <b style={{ color: 'var(--text)' }}>{e.value}</b>
                </span>
              ))}
            </div>
          )}
          {/* The suggested next step. When it carries an executable capability it
              chains straight into the pre-filled campaign editor ("Make it
              happen" — human approval unchanged). Old findings stored `next` as
              a plain string; render both shapes. */}
          {(() => {
            const nx = f.next && (typeof f.next === 'string' ? { text: f.next } : f.next);
            if (!nx || !nx.text) return null;
            return (
              <div style={{ fontSize: 12, border: '1px dashed var(--owl,#5856d6)', borderRadius: 9, padding: '8px 10px', background: 'var(--card,#fff)', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <span style={{ flex: 1, minWidth: 180 }}>Next: {md(nx.text)}</span>
                {nx.href && (
                  <button onClick={() => onNext(nx.href)}
                    style={{ border: 'none', cursor: 'pointer', font: 'inherit', fontSize: 12, fontWeight: 750, padding: '8px 14px', borderRadius: 980, minHeight: 34, background: 'var(--brand)', color: '#fff', flexShrink: 0 }}>
                    {nx.action === 'follow_metric' ? 'Open the board' : (MAKE_IT_HAPPEN[nx.action]?.label || 'Make it happen')}
                  </button>
                )}
                {!nx.href && nx.action === 'schedule_recheck' && !it.recheckAt && (
                  <button onClick={() => onRecheck(nx.days || 3)}
                    style={{ border: 'none', cursor: 'pointer', font: 'inherit', fontSize: 12, fontWeight: 750, padding: '8px 14px', borderRadius: 980, minHeight: 34, background: 'var(--owl,#5856d6)', color: '#fff', flexShrink: 0 }}>
                    Re-check in {nx.days || 3} day{(nx.days || 3) === 1 ? '' : 's'}
                  </button>
                )}
              </div>
            );
          })()}
        </div>
      )}

      {/* Scheduled re-check banner (until it runs, or Cancel). */}
      {it.recheckAt && !dim && (
        <div style={{ fontSize: 11.5, color: 'var(--owl,#5856d6)', display: 'flex', alignItems: 'center', gap: 8 }}>
          <span>Re-checks {recheckLabel} — the fresh finding posts here.</span>
          <button onClick={() => onRecheck(0)} style={{ border: 'none', background: 'none', cursor: 'pointer', font: 'inherit', fontSize: 11.5, color: 'var(--muted)', textDecoration: 'underline', padding: 0 }}>Cancel</button>
        </div>
      )}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 2 }}>
        {dim ? <Btn quiet onClick={onRestore}>Restore</Btn> : done ? null : isInv ? (<>
          {f && <Btn solid={accent} onClick={onToggle}>{open ? 'Hide the finding' : 'Read the finding'}</Btn>}
          {!f && <Btn solid={accent} onClick={onInvestigate} disabled={busy || it.status === 'investigating'}>{busy || it.status === 'investigating' ? 'Working…' : 'Owl, look into this'}</Btn>}
          {f && <Btn quiet onClick={onInvestigate} disabled={busy}>{busy ? 'Working…' : 'Re-run'}</Btn>}
          {f && !it.recheckAt && !pickRecheck && <Btn quiet onClick={() => setPickRecheck(true)}>Re-check later</Btn>}
          <Btn quiet onClick={onDismiss}>Dismiss</Btn>
        </>) : (<>
          {it.capability === 'follow_metric'
            ? <Btn solid={accent} onClick={onFollow}>Follow this metric</Btn>
            : it.href && <Btn solid={accent} onClick={onGo}>{it.goLabel || 'Make it happen'}</Btn>}
          <Btn quiet onClick={onDismiss}>Dismiss</Btn>
        </>)}
      </div>
      {pickRecheck && !it.recheckAt && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', fontSize: 12, color: 'var(--muted)' }}>
          <span>Re-check:</span>
          {[[1, 'Tomorrow'], [3, 'In 3 days'], [7, 'In a week']].map(([d, label]) => (
            <button key={d} onClick={() => { onRecheck(d); setPickRecheck(false); }}
              style={{ border: '1px solid var(--owl,#5856d6)', background: 'transparent', color: 'var(--owl,#5856d6)', cursor: 'pointer', font: 'inherit', fontSize: 11.5, fontWeight: 700, padding: '5px 12px', borderRadius: 980, minHeight: 30 }}>
              {label}
            </button>
          ))}
          <button onClick={() => setPickRecheck(false)} style={{ border: 'none', background: 'none', cursor: 'pointer', font: 'inherit', fontSize: 11.5, color: 'var(--muted)', padding: 0 }}>✕</button>
        </div>
      )}
    </div>
  );
}

function Btn({ solid, quiet, disabled, onClick, children }) {
  return (
    <button onClick={onClick} disabled={disabled}
      style={{ border: quiet ? '1px solid var(--hairline)' : 'none', cursor: disabled ? 'default' : 'pointer', font: 'inherit', fontSize: 12.5, fontWeight: 750, padding: '9px 15px', borderRadius: 980, minHeight: 38,
        background: quiet ? 'transparent' : solid, color: quiet ? 'var(--muted)' : '#fff', opacity: disabled ? 0.6 : 1 }}>
      {children}
    </button>
  );
}
